// Provenance: repo:tds-reconciliation-portal:frontend/src/components/MappingModal.tsx
// (ADR-0005) — restyled (ADR-0010). Column-mapping dialog: shown when a new client's file
// doesn't auto-parse (P10: when ProDoc answers `mapping_needed` — the rows are its header
// candidates, the suggestion its own). The user pins the sheet + header row, maps our fields to their
// headers, re-parses, and the mapping is saved as the client's format profile so the next
// upload parses automatically. Columns are addressed BY POSITION so a blank-header column
// (the party name in a Tally export) stays selectable — labelled by a sample value.
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'

import { postJson } from '../api/client'
import { useInspectUpload, useProfileFields } from '../api/hooks'
import { reparseBody } from '../api/mappingBody'
import type { Upload } from '../api/types'
import { Btn, Modal } from './Modal'
import { Chip } from './badges'
import { showToast } from './toast'

const FIELD_LABELS: Record<string, string> = {
  name: 'Party / deductor name',
  tds_amount: 'TDS amount',
  amount_paid: 'Amount paid',
  section: 'TDS section',
  tan: 'TAN',
  pan: 'PAN',
  date: 'Date',
  payor: 'Party / payor name',
  debit: 'Debit (TDS booked)',
  credit: 'Credit',
  docno: 'Document / voucher no',
  narration: 'Narration',
  utr: 'UTR',
  unit: 'Unit',
  dept: 'Department',
}

export function MappingModal({ upload, clientId, opened, onClose, onParsed }: {
  upload: Upload
  clientId: string
  opened: boolean
  onClose: () => void
  /** The reparsed upload, delivered to whoever holds a stale local copy.
   *  The card renders its local `result` in preference to the refetched
   *  list (activeUpload), so invalidation alone leaves it saying
   *  "needs mapping" after a successful parse. */
  onParsed?: (up: Upload) => void
}) {
  const queryClient = useQueryClient()
  const kind = upload.kind === 'source' ? 'statement' : 'ledger'
  const inspectQ = useInspectUpload(opened ? upload.id : null)
  const fieldsQ = useProfileFields()

  const [sheetName, setSheetName] = useState<string | null>(null)
  const [headerRow, setHeaderRow] = useState<number>(0)
  const [mapping, setMapping] = useState<Record<string, string | null>>({})
  const [profileName, setProfileName] = useState('')
  const [error, setError] = useState<string | null>(null)

  const sheets = inspectQ.data?.sheets ?? []
  const sheet = sheets.find((s) => s.name === sheetName) ?? sheets[0]
  const spec = fieldsQ.data?.[kind]

  // seed defaults from the inspector's suggestion once loaded — by header
  // text, converted to positional ('col:N') so it lines up with the picker
  useEffect(() => {
    if (!sheet) return
    setSheetName(sheet.name)
    const hr = sheet.suggested_header ?? 0
    setHeaderRow(hr)
    setMapping((m) => {
      if (Object.keys(m).length) return m
      const hdr = sheet.rows[hr] ?? []
      const out: Record<string, string> = {}
      for (const [f, h] of Object.entries(sheet.suggested_map ?? {})) {
        const idx = hdr.findIndex((c) => (c ?? '').trim() === String(h).trim())
        if (idx >= 0) out[f] = `col:${idx}`
      }
      return out
    })
  }, [sheet?.name]) // eslint-disable-line react-hooks/exhaustive-deps

  const columnOptions = useMemo(() => {
    const hdr = sheet?.rows[headerRow] ?? []
    const sample = sheet?.rows[headerRow + 1] ?? []
    return hdr.map((h, j) => {
      const header = (h ?? '').toString().trim()
      const eg = (sample[j] ?? '').toString().trim()
      return {
        value: `col:${j}`,
        label: header || `Column ${j + 1}${eg ? ` — e.g. "${eg.slice(0, 22)}"` : ' (no header)'}`,
      }
    })
  }, [sheet, headerRow])

  const missingRequired = (spec?.required ?? []).filter((f) => !mapping[f])

  const saveAndReparse = useMutation({
    mutationFn: async () => {
      // P10 (ADR-0016): one request — ProDoc previews, saves and re-reads (mappingBody.ts)
      return postJson<Upload>(`/uploads/${upload.id}/reparse`,
        reparseBody(kind, sheet?.name, headerRow, mapping, profileName, upload.filename))
    },
    onSuccess: (up) => {
      showToast({
        color: 'green',
        title: 'File mapped & parsed',
        message: `${up.filename} — the mapping is saved for future uploads.`,
      })
      onParsed?.(up)
      void queryClient.invalidateQueries({ queryKey: ['uploads', upload.period_id] })
      void queryClient.invalidateQueries({ queryKey: ['period-summary', upload.period_id] })
      void queryClient.invalidateQueries({ queryKey: ['raw-summary', upload.period_id] })
      void queryClient.invalidateQueries({ queryKey: ['format-profiles', clientId] })
      onClose()
    },
    onError: (e) => setError(String(e instanceof Error ? e.message : e)),
  })

  const select = 'rounded-md border border-border bg-background px-2 py-1 text-sm'

  return (
    <Modal opened={opened} onClose={onClose} wide
           title={`Map columns — ${upload.filename}`}>
      <div className="space-y-3 text-sm">
        <p className="text-muted-foreground">
          This file's layout wasn't recognised. Pick the header row, then tell us which of
          their columns holds each field — columns are listed by position, so a column with
          no header (shown with a sample value) can still be picked. The mapping is saved for
          this client — the next upload parses automatically.
        </p>

        {inspectQ.isError && (
          <p className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-900">
            {String(inspectQ.error)}
          </p>
        )}

        <div className="flex flex-wrap gap-3">
          {sheets.length > 1 && (
            <label className="flex items-center gap-2 text-xs">
              Sheet
              <select className={select} value={sheet?.name ?? ''}
                onChange={(e) => { setSheetName(e.target.value); setMapping({}) }}>
                {sheets.map((s) => <option key={s.name}>{s.name}</option>)}
              </select>
            </label>
          )}
          <label className="flex items-center gap-2 text-xs">
            Header row
            <select className={select} value={headerRow}
              onChange={(e) => { setHeaderRow(Number(e.target.value)); setMapping({}) }}>
              {(sheet?.rows ?? []).map((r, i) => (
                <option key={i} value={i}>
                  Row {i + 1}: {r.filter(Boolean).slice(0, 4).join(' · ').slice(0, 60)}
                </option>
              ))}
            </select>
          </label>
        </div>

        {sheet && (
          <div className="max-h-36 overflow-auto rounded border border-border">
            <table className="w-full text-xs">
              <tbody>
                {sheet.rows.slice(headerRow, headerRow + 4).map((r, i) => (
                  <tr key={i} className={i === 0 ? 'bg-amber-50' : ''}>
                    {r.slice(0, 10).map((c, j) => (
                      <td key={j} className="border border-border px-1.5 py-0.5">{c ?? ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {spec && (
          <div className="space-y-1.5">
            {[...spec.required, ...spec.optional].map((f) => (
              <div key={f} className="flex flex-wrap items-center gap-3">
                <span className="w-52 text-sm">
                  {FIELD_LABELS[f] ?? f}{' '}
                  <Chip tone={spec.required.includes(f) ? 'red' : 'gray'}>
                    {spec.required.includes(f) ? 'required' : 'optional'}
                  </Chip>
                </span>
                <select
                  className={`${select} w-80`}
                  value={mapping[f] ?? ''}
                  onChange={(e) => setMapping((m) => ({ ...m, [f]: e.target.value || null }))}
                >
                  <option value="">— their column —</option>
                  {columnOptions.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}

        <label className="block text-xs">
          Save this mapping as
          <input
            className="mt-1 w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            placeholder="e.g. Apollo Hospitals statement format"
            value={profileName}
            onChange={(e) => setProfileName(e.target.value)}
          />
        </label>

        {error && (
          <p className="rounded-md border border-red-300 bg-red-50 p-2 text-xs text-red-900">
            {error}
          </p>
        )}

        <div className="flex justify-end gap-2">
          <Btn onClick={onClose}>Cancel</Btn>
          <Btn
            kind="primary"
            disabled={missingRequired.length > 0}
            loading={saveAndReparse.isPending}
            onClick={() => {
              setError(null)
              saveAndReparse.mutate()
            }}
          >
            Save mapping &amp; parse
          </Btn>
        </div>
      </div>
    </Modal>
  )
}
